import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, Phone } from "lucide-react";
import { useCustomerAuth } from "../context/CustomerAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls =
  "w-full bg-transparent border-b border-[#E8E4DF] focus:border-[#C5A059] px-0 py-3 text-[#2D1B36] placeholder-[#8A818F] transition-colors duration-300";

export default function AuthPage() {
  const { signIn, setUser } = useCustomerAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("email");
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", identifier: "", password: "" });
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setInfo("");
    try {
      if (mode === "forgot") {
        const res = await axios.post(`${API}/auth/forgot-password`, { identifier: form.identifier });
        setInfo(res.data.message);
      } else {
        const url = mode === "login" ? `${API}/auth/login-password` : `${API}/auth/register`;
        const res = await axios.post(url, form, { withCredentials: true });
        setUser(res.data);
        navigate("/account");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6] grid md:grid-cols-2" data-testid="auth-page">
      <div className="hidden md:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1627012898015-aa708e5c53ee?auto=format&fit=crop&w=1400&q=80"
          alt="Fine lace"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2D1B36]/90 via-[#2D1B36]/55 to-[#2D1B36]/40" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <p className="font-serif-d italic font-light text-4xl lg:text-5xl text-[#FBF9F6] leading-tight">
            Every thread<br />tells a <span className="text-[#DFCA9B]">story.</span>
          </p>
          <p className="mt-6 font-accent text-[10px] tracking-[0.35em] uppercase text-[#FBF9F6]/60">
            Innus Lace Centre · N. Paravur
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-sm"
        >
          <Link
            to="/"
            data-testid="auth-back-home"
            className="inline-flex items-center gap-2 font-accent text-[10px] tracking-[0.25em] uppercase text-[#8A818F] hover:text-[#C5A059] transition-colors mb-10"
          >
            <ArrowLeft size={12} /> Back to store
          </Link>

          <h1 className="font-serif-d font-light text-4xl text-[#2D1B36] tracking-tight">
            {mode === "login" ? "Welcome back" : mode === "register" ? "Join the family" : "Reset password"}
          </h1>
          <p className="mt-3 text-sm text-[#514357] leading-relaxed">
            {mode === "login"
              ? "Sign in to track orders and check out faster."
              : mode === "register"
              ? "Create an account to save your orders and check out faster."
              : "Enter your email and we'll send you a reset link."}
          </p>

          <div className="mt-10 grid grid-cols-2 border border-[#E8E4DF]" data-testid="auth-tabs">
            {[
              { id: "email", label: "Email", icon: Mail },
              { id: "phone", label: "Phone", icon: Phone },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => { setTab(t.id); setError(""); setForm({ ...form, identifier: "" }); }}
                data-testid={`auth-tab-${t.id}`}
                className={`flex items-center justify-center gap-2 py-3 font-accent text-[11px] tracking-[0.2em] uppercase transition-colors duration-300 ${
                  tab === t.id ? "bg-[#4A2B58] text-[#FBF9F6]" : "text-[#4A2B58] hover:bg-[#C5A059]/10"
                }`}
              >
                <t.icon size={13} strokeWidth={1.5} /> {t.label}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-8 space-y-7" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <label className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#8A818F]">Your Name</label>
                <input
                  required
                  minLength={2}
                  placeholder="Full name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="auth-input-name"
                  className={inputCls}
                />
              </div>
            )}
            <div>
              <label className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#8A818F]">
                {tab === "email" ? "Email" : "Phone Number"}
              </label>
              <input
                required
                type={tab === "email" ? "email" : "tel"}
                placeholder={tab === "email" ? "you@example.com" : "10-digit mobile number"}
                value={form.identifier}
                onChange={(e) => setForm({ ...form, identifier: e.target.value })}
                data-testid="auth-input-identifier"
                className={inputCls}
              />
            </div>
            {mode !== "forgot" && (
              <div>
                <label className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#8A818F]">Password</label>
                <input
                  required
                  type="password"
                  minLength={6}
                  placeholder="At least 6 characters"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="auth-input-password"
                  className={inputCls}
                />
                {mode === "login" && (
                  <button
                    type="button"
                    onClick={() => { setMode("forgot"); setError(""); setInfo(""); }}
                    data-testid="auth-forgot-link"
                    className="mt-2 font-accent text-[10px] tracking-[0.2em] uppercase text-[#8A818F] hover:text-[#C5A059] transition-colors"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
            )}
            {error && (
              <p data-testid="auth-error" className="text-sm text-red-700">{error}</p>
            )}
            {info && (
              <p data-testid="auth-info" className="text-sm text-[#2E6669] leading-relaxed">{info}</p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-button"
              className="w-full bg-[#4A2B58] text-[#FBF9F6] font-accent text-xs tracking-[0.25em] uppercase py-4 hover:bg-[#2D1B36] transition-colors duration-300 disabled:opacity-60"
            >
              {busy ? "One moment…" : mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Send Reset Link"}
            </button>
          </form>

          <button
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setInfo(""); }}
            data-testid="auth-mode-toggle"
            className="mt-5 w-full text-center font-accent text-[11px] tracking-[0.15em] uppercase text-[#8A818F] hover:text-[#C5A059] transition-colors"
          >
            {mode === "login" ? "New here? Create an account" : "Back to sign in"}
          </button>

          <div className="my-8 flex items-center gap-4">
            <span className="flex-1 h-px bg-[#E8E4DF]" />
            <span className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#8A818F]">or</span>
            <span className="flex-1 h-px bg-[#E8E4DF]" />
          </div>

          <button
            onClick={signIn}
            data-testid="auth-google-button"
            className="w-full flex items-center justify-center gap-3 border border-[#C5A059]/60 text-[#4A2B58] font-accent text-xs tracking-[0.25em] uppercase py-4 hover:bg-[#C5A059]/10 transition-colors duration-300"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81Z"/></svg>
            Continue with Google
          </button>
        </motion.div>
      </div>
    </div>
  );
}
